import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { NAVIGATE_TO_COVER_FLOW_SEARCH_PARAMS_KEY } from 'restaurantAdmin/utils/constants';
import {
  COVER_FLOW_PATH,
  RESERVATIONS_SERVICE_PATH,
} from 'restaurantAdmin/paths';
import {
  type AdminRestaurant,
  getAdminRestaurant,
} from 'restaurantAdmin/settings/team/apiHelpers';
import { successToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import { useAbortEffect } from '@shared/hooks/useAbortEffect';
import type { PricingInfo } from '@shared/pricingInfoApi';
import { getPricingInfo } from '@shared/pricingInfoApi';
import { reportAppError } from '@shared/reportAppError';
import { isCancellationPolicyApplicable } from '@shared/reservations/reservationUtil';
import { CONFLICT } from '@shared/statusCodes';
import type { CancellationPolicy } from '@shared/types/reservations';
import { getWindow } from '@shared/utils/window';
import {
  CardElement as StripeCardElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import type {
  PaymentMethodResult,
  StripeCardElementChangeEvent,
} from '@stripe/stripe-js';
import { wholeDollarsToCents } from '@utils/currency';
import { ISOTimeTo12HourTime } from '@utils/time';
import { debounce } from 'lodash-es';
import { type FormEventHandler, useCallback, useEffect, useState } from 'react';
import { type Control, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { NAVIGATE_TO_SERVICE_SEARCH_PARAMS_KEY } from '../service/ServicePage';
import type {
  HostBookMultiTableReservationPayload,
  HostBookReservationPayload,
} from './apiHelpers';
import {
  adminSendPayLink,
  type AdminSendPayLinkPayload,
  hostBookMultiTableReservation,
  hostBookReservation,
} from './apiHelpers';
import {
  MultiTableAvailabilityData,
  SingleTableAvailabilityData,
  useAvailabilityDrawerContext,
} from './state/AvailabilityDrawerContext';
import { useGuestTags } from 'restaurantAdmin/hooks/useGuestTags';
import { GuestTag } from 'restaurantAdmin/restaurants/apiHelpers';

const UPDATE_PRICING_INFO_DEBOUNCE_INTERVAL = 1000;

export enum CheckoutTypes {
  FullPrice = 'fullPrice',
  NoPrice = 'noPrice',
  CustomPrice = 'customPrice',
}

interface UseAdminCheckout {
  addGuest: (args: AddGuestArgs) => void;
  control: Control<AdminCheckoutFormData>;
  cancellationPolicy?: CancellationPolicy | null;
  customPriceInDollars: string;
  handleBookReservationClick: FormEventHandler<HTMLFormElement>;
  handleCardElementOnChange: ({
    complete,
  }: StripeCardElementChangeEvent) => void;
  handleCheckoutTypeOnChange: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => Promise<void>;
  isBookButtonDisabled: boolean;
  isGuestFieldsDisabled: boolean;
  pricingInfo?: PricingInfo;
  removeGuest: () => void;
  responseError: string;
  selectedCheckoutType: CheckoutTypes;
  isEnterCardDetailsOn: boolean;
  setIsEnterCardDetailsOn: (next: boolean) => void;
  handleSendPayLinkClick: FormEventHandler<HTMLFormElement>;
  handleCopyPayLinkClick: () => Promise<void>;
  isSendPayLinkPending: boolean;
  issuedPayLinkUrl: string | null;
}

interface AdminCheckoutFormData {
  guestId?: string;
  firstName: string;
  lastName: string;
  customPriceInDollars: string;
  phone?: string;
  guestTags?: (GuestTag | string)[];
  staffNote?: string;
  messageGuest: boolean;
}

interface AddGuestArgs {
  firstName: string;
  lastName: string;
  phone: string | null;
  tags: string[];
}

const DEFAULT_PRICING_INFO: PricingInfo = {
  fee: 0,
  tax: 0,
  total: 0,
};

const useFetchAdminRestaurant = (restaurantId: string) => {
  const [adminRestaurant, setAdminRestaurant] =
    useState<AdminRestaurant | null>(null);

  const refetch = async () => {
    try {
      const response = await getAdminRestaurant(restaurantId);
      setAdminRestaurant(response);
    } catch (error) {
      reportAppError(error);
    }
  };

  useAbortEffect(
    async (signal) => {
      const response = await getAdminRestaurant(restaurantId, signal);
      setAdminRestaurant(response);
    },
    [restaurantId],
  );

  return { adminRestaurant, refetch };
};

export const useAdminCheckout = (): UseAdminCheckout => {
  const restaurant = useRestaurant();
  const { closeDrawer, availabilityData } = useAvailabilityDrawerContext();

  const stripe = useStripe();
  const elements = useElements();

  const [pricingInfo, setPricingInfo] = useState(DEFAULT_PRICING_INFO);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: allTags } = useGuestTags();

  const {
    control,
    handleSubmit,
    resetField,
    formState: { isSubmitting },
    watch,
    setValue,
  } = useForm<AdminCheckoutFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      customPriceInDollars: '',
      phone: '',
      messageGuest: true,
      guestTags: [],
    },
  });

  const guestId = watch('guestId');

  useEffect(() => {
    const subscription = watch(({ customPriceInDollars }, { name }) => {
      if (name === 'customPriceInDollars' && !!customPriceInDollars) {
        void fetchPricingInfoWithDebounce(
          wholeDollarsToCents(Number(customPriceInDollars)),
        );
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const [responseError, setResponseError] = useState('');
  const [selectedCheckoutType, setSelectedCheckoutType] = useState(() =>
    availabilityData && availabilityData.listingPrice > 0
      ? CheckoutTypes.FullPrice
      : CheckoutTypes.NoPrice,
  );
  const [isCardElementFormComplete, setIsCardElementFormComplete] =
    useState(false);
  const [isEnterCardDetailsOn, setIsEnterCardDetailsOn] = useState(false);
  const [isSendPayLinkPending, setIsSendPayLinkPending] = useState(false);
  const [issuedPayLinkUrl, setIssuedPayLinkUrl] = useState<string | null>(null);
  const { adminRestaurant, refetch } = useFetchAdminRestaurant(restaurant.id);

  useEffect(() => {
    if (selectedCheckoutType !== CheckoutTypes.FullPrice) {
      setIsEnterCardDetailsOn(false);
    }
  }, [selectedCheckoutType]);

  useEffect(() => {
    if (availabilityData && selectedCheckoutType === CheckoutTypes.FullPrice) {
      void fetchPricingInfoWithoutDebounce(availabilityData.listingPrice);
    }
    // Only run on mount to populate the default Full Price receipt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPricingInfoWithoutDebounce = async (priceInCents: number) => {
    try {
      const pricingInfoResponse = await getPricingInfo({
        price: priceInCents,
        zipCode: restaurant.postalCode,
        restaurantId: restaurant.id,
      });
      setPricingInfo(pricingInfoResponse);
    } catch (e) {
      reportAppError(e);
    }
  };

  const addGuest = (newGuest: AddGuestArgs) => {
    setValue('firstName', newGuest.firstName);
    setValue('lastName', newGuest.lastName);
    setValue('phone', newGuest.phone || '');
    setValue(
      'guestTags',
      allTags.filter((t) => newGuest.tags.includes(t.title)),
    );
  };

  const removeGuest = () => {
    setValue('firstName', '');
    setValue('lastName', '');
    setValue('phone', '');
    setValue('guestTags', []);
  };

  const fetchPricingInfoWithDebounce = useCallback(
    debounce(async (priceInCents: number) => {
      try {
        const pricingInfoResponse = await getPricingInfo({
          price: priceInCents,
          zipCode: restaurant.postalCode,
          restaurantId: restaurant.id,
        });
        setPricingInfo(pricingInfoResponse);
      } catch (e) {
        reportAppError(e);
      }
    }, UPDATE_PRICING_INFO_DEBOUNCE_INTERVAL),
    [],
  );

  const handleCardElementOnChange = ({
    complete,
  }: StripeCardElementChangeEvent) => {
    setIsCardElementFormComplete(complete);
  };

  const createPaymentMethod = async (): Promise<
    PaymentMethodResult | undefined
  > => {
    if (!stripe || !elements) {
      return undefined;
    }
    const stripeCardElement = elements.getElement(StripeCardElement);
    let stripeResponse;
    if (stripeCardElement) {
      stripeResponse = await stripe.createPaymentMethod({
        type: 'card',
        card: stripeCardElement,
      });

      if (stripeResponse.error?.message) {
        setResponseError(stripeResponse.error.message);
        return undefined;
      }
    }

    return stripeResponse;
  };

  // todo wrap submit
  const handleBookReservationClick = handleSubmit(
    async (data: AdminCheckoutFormData) => {
      if (!availabilityData) return;

      let paymentMethodResponse;

      if (selectedCheckoutType !== CheckoutTypes.NoPrice) {
        paymentMethodResponse = await createPaymentMethod();
        if (!paymentMethodResponse) {
          return;
        }
      }

      let success = false;
      const isMultiTableBooking = !!availabilityData.multiTable;
      try {
        if (isMultiTableBooking) {
          await handleMultiTableBookReservation({
            availabilityData,
            formData: data,
            paymentMethodResponse,
          });
        } else {
          await handleSingleTableBookReservation({
            availabilityData,
            formData: data,
            paymentMethodResponse,
          });
        }
        success = true;
      } catch (e) {
        if (e instanceof ApiError) {
          if (e.statusCode === CONFLICT) {
            refetch();
            setResponseError(
              "We've encountered an error. " +
                'Something may have changed. ' +
                'Please review the updated cancellation policy and confirm your purchase',
            );
          } else {
            setResponseError(e.message);
            reportAppError(e);
          }
        } else {
          setResponseError('Something went wrong.');
          reportAppError(e);
        }
      }
      if (success) {
        const formattedTime = ISOTimeTo12HourTime(availabilityData.time);
        closeDrawer();
        successToast({
          message: `${data.firstName} ${data.lastName}'s ${formattedTime} reservation was booked.`,
        });

        if (searchParams.has(NAVIGATE_TO_SERVICE_SEARCH_PARAMS_KEY)) {
          // Navigating with window.location.assign to force a full page reload.
          // This addresses a bug that caused the buttons in the Occupant
          // Details Drawer to become unresponsive because of the scroll
          // position after client-side navigation.
          // https://app.shortcut.com/peak-reservations/story/13524/occupant-drawer-buttons-not-responsive
          getWindow().location.assign(RESERVATIONS_SERVICE_PATH);
        } else if (searchParams.has(NAVIGATE_TO_COVER_FLOW_SEARCH_PARAMS_KEY)) {
          navigate(`${COVER_FLOW_PATH}?date=${availabilityData.date}`);
        }
      }
    },
  );

  const handleSingleTableBookReservation = async ({
    availabilityData,
    formData,
    paymentMethodResponse,
  }: {
    availabilityData: SingleTableAvailabilityData;
    formData: AdminCheckoutFormData;
    paymentMethodResponse?: PaymentMethodResult;
  }) => {
    const price =
      selectedCheckoutType === CheckoutTypes.CustomPrice
        ? wholeDollarsToCents(Number(formData.customPriceInDollars))
        : availabilityData.listingPrice;

    const getExpectedCancellationPolicyId = () => {
      if (selectedCheckoutType === CheckoutTypes.NoPrice) return null;
      if (!adminRestaurant?.cancellationPolicy) return null;
      if (
        !isCancellationPolicyApplicable(
          price,
          adminRestaurant?.cancellationPolicy?.threshold,
        )
      )
        return null;
      return adminRestaurant.cancellationPolicy.id;
    };

    const hostBookReservationPayload: HostBookReservationPayload = {
      date: availabilityData.date,
      expectedCancellationPolicyId: getExpectedCancellationPolicyId(),
      firstName: formData.firstName,
      guestCount: availabilityData.guestCount,
      lastName: formData.lastName,
      listingId: availabilityData.listingId,
      listingPrice: availabilityData.listingPrice,
      guestId: formData.guestId,
      staffNote: formData.staffNote || undefined,
      paymentMethodId: paymentMethodResponse?.paymentMethod?.id,
      restaurantId: restaurant.id,
      time: availabilityData.time,
      messageGuest: formData.messageGuest,
      isBestAvailable: availabilityData.isBestAvailable,
      guestTags: formData.guestTags?.map((val) =>
        typeof val === 'string'
          ? { id: null, title: val }
          : {
              id: val.id,
              title: val.title,
            },
      ),
    };

    if (formData.phone) {
      hostBookReservationPayload.phone = formData.phone;
    }

    if (selectedCheckoutType === CheckoutTypes.CustomPrice) {
      hostBookReservationPayload.customPrice = wholeDollarsToCents(
        Number(formData.customPriceInDollars),
      );
    }

    return hostBookReservation(hostBookReservationPayload);
  };

  const handleMultiTableBookReservation = async ({
    availabilityData,
    formData,
    paymentMethodResponse,
  }: {
    availabilityData: MultiTableAvailabilityData;
    formData: AdminCheckoutFormData;
    paymentMethodResponse?: PaymentMethodResult;
  }) => {
    const price =
      selectedCheckoutType === CheckoutTypes.CustomPrice
        ? wholeDollarsToCents(Number(formData.customPriceInDollars))
        : 0;

    const hostBookReservationPayload: HostBookMultiTableReservationPayload = {
      date: availabilityData.date,
      firstName: formData.firstName,
      guestCount: availabilityData.guestCount,
      lastName: formData.lastName,
      guestId: formData.guestId,
      staffNote: formData.staffNote || undefined,
      paymentMethodId: paymentMethodResponse?.paymentMethod?.id,
      time: availabilityData.time,
      duration: availabilityData.duration,
      price,
      floorPlanTableIds: availabilityData.tableIds,
      messageGuest: formData.messageGuest,
      guestTags: formData.guestTags?.map((val) =>
        typeof val === 'string'
          ? { id: null, title: val }
          : {
              id: val.id,
              title: val.title,
            },
      ),
    };

    if (formData.phone) {
      hostBookReservationPayload.phone = formData.phone;
    }

    return hostBookMultiTableReservation(
      restaurant.id,
      hostBookReservationPayload,
    );
  };

  const isBookButtonDisabled =
    selectedCheckoutType === CheckoutTypes.NoPrice
      ? isSubmitting
      : !isCardElementFormComplete || isSubmitting;

  const handleCheckoutTypeOnChange = async ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedCheckoutType(value as CheckoutTypes);
    setPricingInfo(DEFAULT_PRICING_INFO);
    resetField('customPriceInDollars');
    if (availabilityData && value === CheckoutTypes.FullPrice) {
      await fetchPricingInfoWithoutDebounce(availabilityData.listingPrice);
    }
  };

  const buildPayLinkPayload = (
    data: AdminCheckoutFormData,
  ): AdminSendPayLinkPayload | null => {
    if (!availabilityData || availabilityData.multiTable) return null;

    const price =
      selectedCheckoutType === CheckoutTypes.CustomPrice
        ? wholeDollarsToCents(Number(data.customPriceInDollars))
        : availabilityData.listingPrice;

    const getExpectedCancellationPolicyId = () => {
      if (!adminRestaurant?.cancellationPolicy) return null;
      if (
        !isCancellationPolicyApplicable(
          price,
          adminRestaurant.cancellationPolicy.threshold,
        )
      )
        return null;
      return adminRestaurant.cancellationPolicy.id;
    };

    return {
      date: availabilityData.date,
      time: availabilityData.time,
      guestCount: availabilityData.guestCount,
      listingId: availabilityData.listingId,
      listingPrice: availabilityData.listingPrice,
      isBestAvailable: availabilityData.isBestAvailable,
      expectedCancellationPolicyId: getExpectedCancellationPolicyId(),
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone ?? '',
      guestId: data.guestId,
      guestTags: data.guestTags?.map((val) =>
        typeof val === 'string'
          ? { id: null, title: val }
          : { id: val.id, title: val.title },
      ),
      staffNote: data.staffNote || undefined,
      customPrice:
        selectedCheckoutType === CheckoutTypes.CustomPrice
          ? wholeDollarsToCents(Number(data.customPriceInDollars))
          : undefined,
    };
  };

  const handlePayLinkError = (e: unknown) => {
    if (e instanceof ApiError) {
      setResponseError(e.message);
    } else {
      setResponseError('Something went wrong.');
      reportAppError(e);
    }
  };

  const handleSendPayLinkClick = handleSubmit(async (data) => {
    const payload = buildPayLinkPayload(data);
    if (!payload) return;
    setIsSendPayLinkPending(true);
    try {
      const result = await adminSendPayLink(restaurant.id, payload);
      setIssuedPayLinkUrl(result.payLinkUrl);
      successToast({ message: 'Pay link sent.' });
    } catch (e) {
      handlePayLinkError(e);
    } finally {
      setIsSendPayLinkPending(false);
    }
  });

  const handleCopyPayLinkClick = async () => {
    if (!issuedPayLinkUrl) return;
    try {
      await navigator.clipboard.writeText(issuedPayLinkUrl);
      successToast({ message: 'Pay link copied to clipboard.' });
    } catch (e) {
      handlePayLinkError(e);
    }
  };

  return {
    addGuest,
    cancellationPolicy: adminRestaurant?.cancellationPolicy,
    control,
    customPriceInDollars: watch('customPriceInDollars'),
    handleBookReservationClick,
    handleCardElementOnChange,
    handleCheckoutTypeOnChange,
    isBookButtonDisabled,
    isGuestFieldsDisabled: !!guestId,
    pricingInfo,
    removeGuest,
    responseError,
    selectedCheckoutType,
    isEnterCardDetailsOn,
    setIsEnterCardDetailsOn,
    handleSendPayLinkClick,
    handleCopyPayLinkClick,
    isSendPayLinkPending,
    issuedPayLinkUrl,
  };
};
